import * as React from 'react';
import { useState } from 'react';
import passengerTypeStore from 'Models/PassengerTypeStore';
import usePassengerTypes from 'Hooks/usePassengerTypes';
import {
	countVisiblePassengerRows,
	hasSeatingInfo,
	PASSENGER_ROWS_BEFORE_SHOW_MORE,
	PassengerInfoBreakdown,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/FerryBookingDetailedView/Sections/PassengerInfoBreakdown';
import { BookingDetailSection, SectionProps } from '../../SharedSections/BookingDetailSection';
import { appConfigStore } from 'Models/AppConfigStore';
import If from 'Views/Components/If/If';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { getTotalPassengerCountFromBooking } from 'Util/_HumanWritten/BookingEntity/GetTotalPassengerCount';
import { useIntl } from 'react-intl';

export function PassengerInfoSection({ booking, canEdit, onEdit, isInvoiced }: SectionProps) {
	const [showMore, setShowMore] = useState(false);

	const intl = useIntl();

	usePassengerTypes();
	if (getTotalPassengerCountFromBooking(booking) === 0) {
		return null;
	}

	if (!booking.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return <></>;
	}

	const description = passengerTypeStore.getPassengerInfoFromBookingSummary(booking.bookedSummary);
	const passengers = booking.GetAllPassengerInfosFromBookedSummary();

	const showBreakdown = passengers.length > 0 && (appConfigStore.passengerInfoEnabled || hasSeatingInfo(passengers));
	/*
		How many rows the list will actually draw — not how many passengers there are.

		A party of four where only two carry a name or a seat draws two rows, so counting passengers
		offered `Show more` with nothing behind it. The list already counts this way; the button and
		the summary below now ask the same question it does.
	*/
	const rowCount = countVisiblePassengerRows(passengers);
	// The fare-type layout names how many passengers are behind the toggle, rather than a bare
	// "Show more" that does not say what it would reveal. Its own keys because the other layout keeps
	// the shorter wording, and a count is wrong for a list that is not a list of passengers.
	const useFareLayout = appConfigStore.passengerClassesEnabled;

	const sectionContent = (
		<p className="passengers-on-trip">
			{description}
			{/*
				Shown when the site collects passenger details OR when the booking holds a class or a
				seat. Gating on passengerInfoEnabled alone hid the class and seat on every site that
				does not ask for names, which is most of them.
			*/}
			{showBreakdown && (
				<div className="passenger-info-list__container">
					<PassengerInfoBreakdown
						passengers={passengers}
						showMore={showMore}
						showPassengerType
						tripHasSeats={booking.bookedSummary?.tripHasSeats}
					/>
					<If condition={rowCount > PASSENGER_ROWS_BEFORE_SHOW_MORE}>
						<Button
							className={useFareLayout ? 'passenger-list__toggle' : 'see-more__button'}
							onClick={() => setShowMore(!showMore)}
							display={Display.Text}
							colors={Colors.Secondary}
						>
							{useFareLayout
								? showMore
									? intl.formatMessage({
											id: 'booking-details__passengers-section__show-fewer-button__label',
											defaultMessage: 'Show fewer',
										})
									: intl.formatMessage(
											{
												id: 'booking-details__passengers-section__show-all-button__label',
												defaultMessage: 'Show all {count} passengers',
											},
											{ count: rowCount },
										)
								: showMore
									? intl.formatMessage({
											id: 'booking-details__passengers-section__show-less-button__label',
											defaultMessage: 'Show less',
										})
									: intl.formatMessage({
											id: 'booking-details__passengers-section__show-more-button__label',
											defaultMessage: 'Show more',
										})}
							{useFareLayout && (
								<Icon
									name="expand_more"
									useMaterialIcons
									classname={`passenger-list__toggle-chevron${
										showMore ? ' passenger-list__toggle-chevron--open' : ''
									}`}
								/>
							)}
						</Button>
					</If>
				</div>
			)}
		</p>
	);

	let sectionLabel = intl.formatMessage({
		id: 'booking-details__passengers-section__label',
		defaultMessage: 'Passengers',
	});

	if (booking.hasVehicle()) {
		sectionLabel = intl.formatMessage({
			id: 'booking-details__driver-and-passengers-section__label',
			defaultMessage: 'Driver & passengers',
		});
	}

	return (
		<BookingDetailSection
			sectionName={sectionLabel}
			sectionContent={sectionContent}
			sectionClassName="passengers"
			icon={{
				name: 'person',
				useMaterialIcons: true,
			}}
			modificationRules={{
				canEdit: canEdit,
				editType: 'passenger',
				onEdit: onEdit,
				isInvoiced: isInvoiced,
			}}
		/>
	);
}
